import {Component, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {PatientService} from '../../patients/services/patient.service';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {CoreService} from 'src/app/core/services/core.service';
import {MedicationItemAddCardComponent} from '../medication-item-add-card/medication-item-add-card.component';
import {MatDialog} from '@angular/material/dialog';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {Title} from '@angular/platform-browser';
import {MedicationRepositoryService, Medication, Patient, ScriptRepositoryService, ScriptType, UserSettings, WebSocketService, Events} from '@xmedical/core';

@Component({
    selector: 'patient-medication-widget',
    templateUrl: './medication-widget.component.html',
    styleUrls: ['./medication-widget.component.scss'],
    standalone: false
})
export class MedicationWidgetComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() scriptType: ScriptType;
  error = '';
  loading = false;
  settings: UserSettings;
  medication: Medication[] = [];
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];
  private channelName = '';
  private readonly eventName = Events.MedicationMedicationListChanged;

  private medicationRepository = inject(MedicationRepositoryService);
  private matDialog = inject(MatDialog);
  private scriptsRepository = inject(ScriptRepositoryService);
  private patientService = inject(PatientService);
  private patientEventService = inject(PatientEventsService);
  private coreService = inject(CoreService);
  private pushService = inject(WebSocketService);

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasMedication(): boolean {
    return this.medication.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  get confirmationMessage(): string {
    return 'Are you sure you want to save a chronic script with this medication for ' + this.patient.firstName + ' ' + this.patient.surname + '?';
  }

  searchMedication$ = (query: string): Observable<Medication[]> => {
    return this.medicationRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.settings = this.coreService.settings();
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => {
      this.loadPatientMedication();
    }));
    this.subscriptions.push(this.patientService.patientMedication$.subscribe(medication => {
      this.medication = medication.filter(item => item.scriptType === this.scriptType);
    }));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(e => e.type === 'medication_item_deleted')).subscribe(() => this.loadPatientMedication()));
    this.pushService.subscribe(this.channelName, this.eventName, _ => {
      this.loadPatientMedication();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  onAddNewItem() {
    const dialogRef = this.matDialog.open(MedicationItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive'
    });
    this.subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.autocomplete.search(result.description);
      }
    }));
  }

  onItemSelected(medication: Medication) {
    medication.createdUser = this.coreService.currentUser;
    medication.lastModifiedUser = this.coreService.currentUser;
    medication.createdDate = new Date();
    medication.lastModifiedDate = new Date();
    this.medication.push(medication);
    this.addMedicationToPatient(medication);
    this.autocomplete.cancelSearch();
  }

  hasDuplicates(item: Medication): boolean {
    return this.medication.some(m => {
      if (m.description.length > item.description.length) {
        return m.description.indexOf(item.description) > -1 && m.id !== item.id;
      } else if (m.description.length < item.description.length) {
        return item.description.indexOf(m.description) > -1 && m.id !== item.id;
      } else {
        return m.description === item.description && m.id !== item.id;
      }
    });
  }

  hasDuplicateTypes(item: Medication): boolean {
    return this.medication.some(m => {
      return !item.type.unassigned && item.typeId === m.typeId && item.id !== m.id;
    });
  }

  saveScript() {
    this.scriptsRepository.saveScript(this.patientId, this.medication, this.scriptType).subscribe({
      next: (saved) => {
        this.patientEventService.emitEvent(new PatientEvent('scripts_updated'));
      },
      error: (error) => {
        this.coreService.showErrorToast('Could not save script');
        console.error(error);
      }
    });
  }

  private loadPatientMedication() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.patientService.loadPatientMedication();
  }

  private addMedicationToPatient(medication: Medication) {
    this.error = '';
    medication.scriptType = this.scriptType;
    this.medicationRepository.addMedicationToPatient(this.patientId, medication).subscribe({
      next: (newId) => {
        this.patientEventService.emitEvent(new PatientEvent('medication_item_added'));
        medication.id = newId;
      },
      error: (error) => {
        this.error = 'Could not add new medication item to this patient';
        this.loadPatientMedication();
      }
    });
  }
}
